import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, organizationControlAccess, type OrganizationRole, type ProvisioningKind } from "@nahal/api-client";
import { toast } from "@nahal/ui";
import { api } from "./api";

export const keys = {
  me: ["me"] as const,
  members: ["members"] as const,
  products: ["products"] as const,
  subscriptions: ["subscriptions"] as const,
  workspaces: ["workspaces"] as const,
  requests: ["requests"] as const,
  audit: (page: number) => ["audit", page] as const,
  sessions: ["sessions"] as const,
  domains: ["domains"] as const,
  workspaceModules: (workspaceId: string) => ["workspace-modules", workspaceId] as const,
  domainTargets: (workspaceId: string) => ["domain-targets", workspaceId] as const,
};

export const useMe = () => useQuery({ queryKey: keys.me, queryFn: api.me });
export const useMembers = () => useQuery({ queryKey: keys.members, queryFn: api.members });
export const useProducts = () => useQuery({ queryKey: keys.products, queryFn: api.products });
export const useSubscriptions = () =>
  useQuery({ queryKey: keys.subscriptions, queryFn: api.subscriptions });
export const useWorkspaces = () => useQuery({ queryKey: keys.workspaces, queryFn: api.workspaces });
export const useRequests = () => useQuery({ queryKey: keys.requests, queryFn: api.requests });
export const useAudit = (page: number) =>
  useQuery({ queryKey: keys.audit(page), queryFn: () => api.audit(page) });

/** One place that turns an ApiError into a toast, so no screen has to remember. */
export function useApiMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  options: { invalidate?: ReadonlyArray<readonly unknown[]>; success?: string } = {},
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      options.invalidate?.forEach((key) => queryClient.invalidateQueries({ queryKey: [...key] }));
      if (options.success) toast.success(options.success);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        options.invalidate?.forEach((key) => queryClient.invalidateQueries({ queryKey: [...key] }));
      }
      toast.error(error instanceof ApiError ? error.message : "Something went wrong.");
    },
  });
}

export const useUpdateOrganization = () =>
  useApiMutation(api.updateOrganization, { invalidate: [keys.me], success: "Organisation updated." });

export const useAddMember = () =>
  useApiMutation((args: { email: string; role: OrganizationRole }) => api.addMember(args), {
    invalidate: [keys.members],
    success: "Member added.",
  });

export const useRemoveMember = () =>
  useApiMutation((memberId: string) => api.removeMember(memberId), {
    invalidate: [keys.members],
    success: "Member removed.",
  });

export const useSubmitRequest = () =>
  useApiMutation(
    (args: { kind: ProvisioningKind; payload?: string; note?: string }) => api.submitRequest(args),
    { invalidate: [keys.requests], success: "Request sent. We will get back to you." },
  );

export const useCancelRequest = () =>
  useApiMutation((requestId: string) => api.cancelRequest(requestId), {
    invalidate: [keys.requests],
    success: "Request cancelled.",
  });

export const useSessions = () => useQuery({ queryKey: keys.sessions, queryFn: api.sessions });

export const useRevokeSession = () =>
  useApiMutation((sessionId: string) => api.revokeSession(sessionId), {
    invalidate: [keys.sessions],
    success: "Session signed out.",
  });

export const useChangePassword = () =>
  useApiMutation(
    (args: { currentPassword: string; newPassword: string }) => api.changePassword(args),
    { success: "Password changed. Sign in again." },
  );

export const useDomains = () => useQuery({ queryKey: keys.domains, queryFn: api.domains });

export const useDomainTargets = (workspaceId: string) =>
  useQuery({
    queryKey: keys.domainTargets(workspaceId),
    queryFn: () => api.domainTargets(workspaceId),
    enabled: Boolean(workspaceId),
  });

export const useAddDomain = () =>
  useApiMutation(
    (args: { workspaceId: string; hostname: string; kind?: string; targetRef?: string }) =>
      api.addDomain(args),
    { invalidate: [keys.domains], success: "Domain added. Create the DNS records we show you." },
  );

export const useRemoveDomain = () =>
  useApiMutation((domainId: string) => api.removeDomain(domainId), {
    invalidate: [keys.domains],
    success: "Domain removed.",
  });

export const useWorkspaceModules = (workspaceId: string) =>
  useQuery({
    queryKey: keys.workspaceModules(workspaceId),
    queryFn: () => api.workspaceModules(workspaceId),
    enabled: Boolean(workspaceId),
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: false,
  });

export const useSetWorkspaceModules = (workspaceId: string) =>
  useApiMutation(
    (body: Parameters<typeof api.setWorkspaceModules>[1]) => api.setWorkspaceModules(workspaceId, body),
    { invalidate: [keys.workspaceModules(workspaceId)], success: "Module settings saved." },
  );

export function useOrganizationAccess() {
  const me = useMe();
  const enabled = useModuleFeature();
  return organizationControlAccess(me.data?.role, enabled);
}

export function useModuleFeature() {
  const features = useQuery({
    queryKey: ["platform-features"],
    queryFn: () => api.features().catch(() => ({ moduleAccessEnabled: false })),
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
    networkMode: "always",
  });
  return features.data?.moduleAccessEnabled === true && !features.isError;
}
